import type React from 'react'
import {
  createContext,
  useEffect,
  useState,
  type PropsWithChildren
} from 'react'
import { ConfigProvider, theme } from 'antd'

type ColorMode = 'light' | 'dark'

interface ColorModeContextType {
  mode: ColorMode
  setMode: (mode: ColorMode) => void
  toggleTheme: () => void
}

export const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined)

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const [mode, setMode] = useState<ColorMode>('light')

  useEffect(() => {
    // Check local storage or system preference
    const storedMode = localStorage.getItem('colorMode') as ColorMode | null
    if (storedMode) {
      setMode(storedMode)
    } else if (
      window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ) {
      setMode('dark')
    }
  }, [])

  const toggleTheme = (): void => {
    const newMode = mode === 'light' ? 'dark' : 'light'
    setMode(newMode)
    localStorage.setItem('colorMode', newMode)
  }

  return (
    <ColorModeContext.Provider
      value={{
        setMode,
        mode,
        toggleTheme
      }}
    >
      <ConfigProvider
        theme={{
          algorithm:
            mode === 'light' ? theme.defaultAlgorithm : theme.darkAlgorithm
        }}
      >
        {children}
      </ConfigProvider>
    </ColorModeContext.Provider>
  )
}
